import type { LitElement } from 'lit'
import { property } from 'lit/decorators.js'

type Constructor<T = Record<string, unknown>> = new (...args: any[]) => T

export declare class InputMixinInterface {
  name?: string
  value: string
  disabled: boolean
  protected _formId?: string
  set form(id: string)
  get form(): HTMLFormElement | null
}

export function InputMixin<T extends Constructor<LitElement>>(superClass: T): Constructor<InputMixinInterface> & T {
  class InputElement extends superClass {
    /**
     * Makes the component disabled. This prevents users from
     * being able to interact with the component, and conveys
     * its inactive state to assistive technologies.
     */
    @property({ type: Boolean, reflect: true }) disabled = false

    /**
     * The name of the form component.
     */
    @property({ reflect: true }) name?: string

    /**
     * The value of the form component.
     */
    @property() value: string = ''

    private formAncestor: HTMLFormElement | null = null
    protected _formId?: string
    set form(id: string) {
      const oldVal = this._formId
      this._formId = id
      this.requestUpdate('form', oldVal)
    }

    /**
     * Gets the form, if any, associated with the form element.
     * The setter accepts a string, which is the id of the form.
     */
    @property()
    get form(): HTMLFormElement | null {
      if (this._formId) {
        const root = this.getRootNode() as ShadowRoot | Document
        return root.querySelector<HTMLFormElement>(`form#${this._formId}`)
      }
      return this.formAncestor
    }

    connectedCallback() {
      super.connectedCallback()
      // we can cache this, since it will only change if the element is (re)connected
      this.formAncestor = this.closest('form')
    }

    disconnectedCallback() {
      super.disconnectedCallback()
      this.formAncestor = null
    }
  }

  return InputElement as unknown as Constructor<InputMixinInterface> & T
}
